/**
 * AutoPlayer — drives the wordchase keyboard with human-like timing.
 *
 * IMPORTANT: `kbRef` is a React ref whose `.current` is updated on every render
 * with a fresh KeyBoard instance. We MUST read `kbRef.current` before each
 * keystroke action, because KeyBoard captures `cursor` and `word` in its
 * constructor closure — a stale `kb` would type into the same position.
 */

// Gaussian-ish random: Box-Muller transform, clamped
function gaussRand(mean, stddev, min, max) {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  let num = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  num = num * stddev + mean;
  return Math.max(min, Math.min(max, Math.round(num)));
}


class AutoPlayer {
  constructor(origin, apiTracker) {
    this.origin = origin;
    this.apiTracker = apiTracker;
    this._cancelled = false;
    this._running = false;
    this._status = 'idle'; // idle, thinking, typing, submitting, paused, done
    this._onStatus = null;
    this._activeTimeout = null;
  }

  _setStatus(status) {
    this._status = status;
    if (this._onStatus) this._onStatus(status);
  }

  get status() { return this._status; }
  get running() { return this._running; }

  /**
   * Start auto-playing one move.
   * @param {Object} kbRef - React ref to KeyBoard instance (read .current before each action!)
   * @param {Object} gameRef - React ref to current game state
   * @param {Object} constraintsRef - React ref to current constraints
   * @param {number} vocabLevel - 1 (easy) to 5 (hard)
   * @param {Function} onStatusChange - callback(status)
   * @param {Function} isComplete - function that returns true if game is won
   * @param {boolean} allowFalseStarts - whether to use false starts
   */
  async start(kbRef, gameRef, constraintsRef, vocabLevel, onStatusChange, isComplete, allowFalseStarts = true) {
    if (this._running) return;
    this._running = true;
    this._cancelled = false;
    this._onStatus = onStatusChange;
    this._setStatus('thinking');

    try {
      while (!this._cancelled) {
        const shouldContinue = await this._playOneMove(kbRef, gameRef, constraintsRef, vocabLevel, isComplete, allowFalseStarts);
        if (!shouldContinue) break;
      }
    } catch (e) {
      if (!this._cancelled) {
        console.error('[AutoPlayer] error:', e);
        this._setStatus('idle');
      }
    }
    this._running = false;
  }

  stop() {
    this._cancelled = true;
    this._running = false;
    this._setStatus('idle');
    if (this._activeTimeout) {
      clearTimeout(this._activeTimeout);
      this._activeTimeout = null;
    }
  }

  async _playOneMove(kbRef, gameRef, constraintsRef, vocabLevel, isComplete, allowFalseStarts) {
    if (this._cancelled) return false;

    // Check if game is already complete
    if (isComplete()) {
      this._setStatus('done');
      return false;
    }

    this._setStatus('thinking');

    // 1. Ask server for next move
    const resp = await this._fetchAutoplay(vocabLevel, allowFalseStarts);
    if (this._cancelled) return false;

    if (resp.game_complete) {
      this._setStatus('done');
      return false;
    }

    const word = resp.word;
    const falseStart = resp.false_start;
    const falseStartLen = resp.false_start_len || 0;
    const thinkMs = resp.think_ms || 1000;

    if (!word) {
      console.warn('[AutoPlayer] no word returned from server');
      this._setStatus('idle');
      return false;
    }

    // Validate word length matches game
    const game = gameRef.current;
    if (game && word.length !== game.n) {
      console.error(`[AutoPlayer] word length mismatch: word="${word}" (${word.length}), game.n=${game.n}`);
      this._setStatus('idle');
      return false;
    }

    // 2. Think delay
    await this._sleepCancellable(thinkMs);
    if (this._cancelled) return false;

    // 3. Clear any existing state — read FRESH kb
    this._setStatus('typing');
    let kb = kbRef.current;
    if (kb && !kb.disabled_escape()) {
      kb.escape();
      await this._sleepCancellable(250);
      if (this._cancelled) return false;
    }

    // 4. Determine which positions are active (need letters typed)
    const constraints = constraintsRef.current || constraintsRef;
    const pos = constraints.pos || [];
    const n = pos.length;

    // Build list of active positions (non-green) and what letter to type at each
    const activePositions = [];
    for (let i = 0; i < n; i++) {
      if (pos[i] && pos[i].length > 1) {
        activePositions.push(i);
      }
    }

    // 5. Type the false start (if any)
    if (falseStart && falseStartLen > 0 && falseStart.length === n) {
      let typed = 0;
      for (let pi = 0; pi < activePositions.length && typed < falseStartLen; pi++) {
        if (this._cancelled) return false;
        const posIdx = activePositions[pi];
        const ch = falseStart[posIdx];
        if (ch && pos[posIdx] && pos[posIdx].includes(ch)) {
          // Read FRESH kb before each keystroke
          kb = kbRef.current;
          if (!kb || kb.disabled_letter(ch)) break;
          kb.letter(ch, () => {}, false);
          typed++;
          await this._sleepCancellable(gaussRand(130, 40, 70, 250));
          if (this._cancelled) return false;
        } else {
          break;
        }
      }

      if (typed > 0) {
        // Pause as if "noticing the mistake"
        await this._sleepCancellable(gaussRand(500, 150, 300, 900));
        if (this._cancelled) return false;

        // Backspace all false-start letters
        for (let i = 0; i < typed; i++) {
          if (this._cancelled) return false;
          kb = kbRef.current;
          if (kb && !kb.disabled_backspace()) {
            kb.backspace();
            await this._sleepCancellable(gaussRand(80, 20, 50, 140));
          }
        }

        // Pause after clearing
        await this._sleepCancellable(gaussRand(400, 100, 200, 700));
        if (this._cancelled) return false;
      }
    }

    // 6. Type the real word — iterate active positions only
    for (let pi = 0; pi < activePositions.length; pi++) {
      if (this._cancelled) return false;
      const posIdx = activePositions[pi];
      const ch = word[posIdx];
      if (ch && ch !== ' ') {
        // Read FRESH kb before each keystroke
        kb = kbRef.current;
        if (!kb) { console.warn('[AutoPlayer] kb is null'); break; }
        if (kb.disabled_letter(ch)) {
          console.warn(`[AutoPlayer] letter '${ch}' disabled at position ${posIdx}, skipping`);
          continue;
        }
        kb.letter(ch, () => {}, false);

        // Human-like inter-key delay
        const isSlowPause = Math.random() < 0.15;
        const delay = isSlowPause
          ? gaussRand(350, 80, 250, 550)
          : gaussRand(120, 35, 65, 220);
        await this._sleepCancellable(delay);
        if (this._cancelled) return false;
      }
    }

    // 7. Pause before submitting
    await this._sleepCancellable(gaussRand(350, 100, 200, 600));
    if (this._cancelled) return false;

    // 8. Submit — read FRESH kb, await the gamemove response
    this._setStatus('submitting');
    kb = kbRef.current;
    if (!kb) { this._setStatus('idle'); return false; }
    try {
      if (!kb.disabled_enter()) {
        await kb.enter();
      } else if (!kb.disabled_stab()) {
        await kb.stab();
      } else {
        console.warn('[AutoPlayer] enter/stab disabled after typing — escaping');
        if (!kb.disabled_escape()) kb.escape();
        this._setStatus('idle');
        return false;
      }
    } catch (e) {
      console.error('[AutoPlayer] gamemove error:', e);
      this._setStatus('idle');
      return false;
    }
    if (this._cancelled) return false;

    // Small settle delay for React to re-render with updated constraints
    await this._sleepCancellable(300);
    if (this._cancelled) return false;

    // 10. Check if game won
    if (isComplete()) {
      this._setStatus('done');
      return false;
    }

    // 11. Continue to next move
    return true;
  }

  async _fetchAutoplay(vocabLevel, allowFalseStarts) {
    try {
      const body = JSON.stringify({
        vocab_level: vocabLevel,
        allow_false_starts: allowFalseStarts
      });
      if (this.apiTracker) {
        const { data } = await this.apiTracker.trackedFetch(
          this.origin + '/autoplay',
          { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body },
          'autoplay'
        );
        return data?.o || data || {};
      } else {
        const resp = await fetch(this.origin + '/autoplay', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body
        });
        const data = await resp.json();
        return data?.o || data || {};
      }
    } catch (e) {
      console.error('[AutoPlayer] fetch error:', e);
      return { game_complete: false, word: null };
    }
  }

  _sleepCancellable(ms) {
    return new Promise((resolve) => {
      this._activeTimeout = setTimeout(() => {
        this._activeTimeout = null;
        resolve();
      }, ms);
    });
  }
}

export default AutoPlayer;
